import { useId, type KeyboardEvent } from "react";
import { useReducedMotion } from "motion/react";
import { RadioGroup as RadioPrimitive } from "@/components/animate-ui";
import { indicatorTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

export type RadioOption = {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
};

/**
 * Radix roving focus (1.2.1) wraps backward from the first radio but not
 * forward from the last one. WAI-ARIA radio groups wrap both ways, so the
 * forward wrap is handled here.
 */
function wrapForward(event: KeyboardEvent<HTMLDivElement>, orientation: "vertical" | "horizontal") {
  const forward = orientation === "vertical" ? ["ArrowDown", "ArrowRight"] : ["ArrowRight", "ArrowDown"];
  if (!forward.includes(event.key)) return;
  const radios = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]:not([disabled])')];
  if (radios.length < 2 || event.target !== radios[radios.length - 1]) return;
  event.preventDefault();
  radios[0]?.focus();
  radios[0]?.click();
}

export function RadioGroup({
  label,
  options,
  value,
  defaultValue,
  onValueChange,
  orientation = "vertical",
  disabled = false,
  className,
}: {
  label: string;
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  orientation?: "vertical" | "horizontal";
  disabled?: boolean;
  className?: string;
}) {
  const groupId = useId();
  const reduce = useReducedMotion();
  return (
    <fieldset className={cn("grid gap-3", className)} data-slot="radio-group" disabled={disabled}>
      <legend className="mb-3 text-sm text-foreground">{label}</legend>
      <RadioPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        orientation={orientation}
        disabled={disabled}
        onKeyDownCapture={(event) => wrapForward(event, orientation)}
        className={cn("flex gap-3", orientation === "vertical" ? "flex-col" : "flex-row flex-wrap gap-x-6")}
      >
        {options.map((option) => {
          const itemId = `${groupId}-${option.value}`;
          return (
            <div key={option.value} className="flex items-start gap-3">
              <RadioPrimitive.Item
                id={itemId}
                value={option.value}
                disabled={option.disabled}
                aria-describedby={option.hint ? `${itemId}-hint` : undefined}
                className="relative mt-0.5 grid size-5 shrink-0 place-items-center rounded-pill border border-line-strong bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-outline)] data-[state=checked]:border-control data-[state=checked]:bg-control disabled:border-line disabled:bg-surface-muted"
              >
                <RadioPrimitive.Indicator transition={indicatorTransition(reduce)} className="block size-2 rounded-pill bg-[var(--control-glyph)]" />
              </RadioPrimitive.Item>
              <div className="grid gap-1">
                <label htmlFor={itemId} className={cn("text-sm", option.disabled || disabled ? "text-muted" : "text-foreground")}>
                  {option.label}
                </label>
                {option.hint ? (
                  <p id={`${itemId}-hint`} className="text-sm text-muted">
                    {option.hint}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </RadioPrimitive.Root>
    </fieldset>
  );
}
